import * as React from "react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Star, Award, Leaf, Mail, MessageCircle, Phone, ArrowRight, Crown, Sparkles, Palette } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import ProductCatalog from "@/components/ProductCatalog";
import Navigation from "@/components/Navigation";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";
import { QuickAnswer } from "@/components/seo/QuickAnswer";
import { FAQSection } from "@/components/seo/FAQSection";
import luxuryBathroomCtaImage from "@/assets/luxury-bathroom-cta.webp";
import { trackPhoneClick, trackWhatsAppClick } from "@/components/ConversionTracking";

// Product images
import lunaMainImage from "@/assets/luna-m9-main-optimized.webp";
import luxaMainImage from "@/assets/luxa-series-product-main.jpg";
import ariaMainImage from "@/assets/aria-series-product-main.jpg";

export default function SmartToilets() {
  const { t, language } = useI18n();
  const seo = getSeoCopy("smartToilets", language);
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  // Core product lineup
  const coreProducts = [
    {
      name: "REM7",
      tagline: "Flagship Smart Toilet",
      description: "The pinnacle of smart toilet innovation. Instant heating, personalized settings, and advanced wellness features.",
      image: lunaMainImage,
      href: "/products/rem7",
      icon: Crown,
      badge: "Flagship",
      badgeColor: "bg-accent text-accent-foreground"
    },
    {
      name: "Luxa",
      tagline: "Luxury Premium Line",
      description: "Motion sensors, tankless heating, UV sterilization, and premium automation for the discerning buyer.",
      image: luxaMainImage,
      href: "/products/luxa",
      icon: Sparkles,
      badge: "Luxury",
      badgeColor: "bg-primary text-primary-foreground"
    },
    {
      name: "Aria",
      tagline: "Design-Focused Variant",
      description: "Minimalist architectural design with maximum comfort. Wall-hung elegance meets intelligent functionality.",
      image: ariaMainImage,
      href: "/products/aria",
      icon: Palette,
      badge: "Design",
      badgeColor: "bg-muted text-muted-foreground"
    }
  ];

  const categories = [
    { id: "", label: "All Products", color: "bg-gradient-to-r from-primary to-accent" },
    { id: "flagship", label: "Flagship", color: "bg-gradient-to-r from-accent to-accent/80" },
    { id: "luxury", label: "Luxury", color: "bg-gradient-to-r from-primary to-primary/80" },
    { id: "wellness-focused", label: "Wellness", color: "bg-gradient-to-r from-accent/80 to-accent/60" },
    { id: "tech-forward", label: "Technology", color: "bg-gradient-to-r from-primary/80 to-accent/80" },
    { id: "square-design", label: "Square Design", color: "bg-gradient-to-r from-amber-500 to-amber-600" },
    { id: "egg-design", label: "Egg Design", color: "bg-gradient-to-r from-teal-500 to-cyan-500" }
  ];

  // ItemList structured data for product collection
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "REM7 Smart Toilet Collection | Aureum VII Trading",
    "description": "Complete collection of REM7 smart toilets from Aureum VII Trading featuring advanced bidet technology, motion sensors, instant heating, and premium automation.",
    "url": "https://www.aureum7trading.com/smart-toilets",
    "numberOfItems": 3,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "REM7 Smart Toilet",
        "url": "https://www.aureum7trading.com/products/rem7"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Luxa Smart Toilet",
        "url": "https://www.aureum7trading.com/products/luxa"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Aria Smart Toilet",
        "url": "https://www.aureum7trading.com/products/aria"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead route="smartToilets" schema={itemListSchema} />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 px-4 sm:px-6 lg:px-12 bg-white text-foreground overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{ backgroundImage: `url(${luxuryBathroomCtaImage})` }}
        ></div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-6 text-lg px-4 py-2">
              REM7 Collection · Specified for European projects
            </Badge>
            <h1 className="font-playfair text-4xl sm:text-6xl md:text-7xl font-light mb-6 tracking-tight text-primary">
              {seo.h1}
            </h1>
            <p className="font-inter text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Tankless wall-hung systems with integrated bidet, heated seat, UV sterilization and auto open/close — 
              the agile alternative to TOTO Neorest and Geberit AquaClean, engineered for villas, boutique hotels and developers across the EU.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm opacity-75">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span>European sales &amp; support</span>
              </div>
              <Separator orientation="vertical" className="h-4 bg-border" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <Award className="w-5 h-5" />
                <span>Premium Design</span>
              </div>
              <Separator orientation="vertical" className="h-4 bg-border" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <Leaf className="w-5 h-5" />
                <span>Eco-Luxury</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-12">
        <BreadcrumbNav items={[
          { label: "Home", href: "/" },
          { label: "Products" }
        ]} />

        {/* TL;DR / Quick Answer optimized for AI answer engines */}
        <QuickAnswer
          question="What are Aureum VII's luxury smart toilets?"
          className="mb-10"
        >
          Aureum VII Trading supplies tankless, wall-hung and floor-standing{" "}
          <strong>luxury smart toilets</strong> with integrated bidet, heated seat,
          UV self-cleaning nozzles and auto open/close. The flagship{" "}
          <LocalizedLink to="/products/luxa" className="text-accent underline underline-offset-2">
            LUXA M80
          </LocalizedLink>{" "}
          is available at €600 per unit and ships across the EU — a European alternative to
          TOTO Neorest and Geberit AquaClean, engineered for villas, boutique
          hotels and developers.
        </QuickAnswer>


        {/* Core Product Lineup - Featured Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <Badge variant="outline" className="border-accent/30 text-accent bg-accent/10 mb-4">
              Core Lineup
            </Badge>
            <h2 className="font-playfair text-3xl md:text-4xl font-medium text-primary mb-4">
              The REM7 Collection
            </h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              Three distinct models designed to meet every need—from flagship performance to design excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreProducts.map((product, index) => (
              <Card 
                key={product.name} 
                className={`group overflow-hidden border-border/50 hover:shadow-xl transition-all duration-500 ${
                  index === 0 ? 'md:scale-105 md:shadow-lg border-accent/30' : ''
                }`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img loading="lazy" decoding="async" 
                    src={product.image} 
                    alt={`${product.name} luxury smart toilet with heated bidet seat by Aureum VII Trading`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className={product.badgeColor}>
                      <product.icon className="w-3 h-3 mr-1" />
                      {product.badge}
                    </Badge>
                  </div>
                  {index === 0 && (
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-accent text-accent-foreground">
                        <Crown className="w-3 h-3 mr-1" />
                        Best Seller
                      </Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-6">
                  <h3 className="font-playfair text-2xl font-semibold text-primary mb-1">
                    {product.name}
                  </h3>
                  <p className="font-inter text-sm text-accent mb-3">
                    {product.tagline}
                  </p>
                  <p className="font-inter text-sm text-muted-foreground mb-4 leading-relaxed">
                    {product.description}
                  </p>
                  <Button asChild className="w-full group/btn">
                    <LocalizedLink to={product.href}>
                      View Details
                      <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* REM7 Variants Section */}
        <section className="mb-16 py-12 bg-muted/30 rounded-2xl px-6 md:px-12">
          <div className="text-center mb-8">
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10 mb-4">
              Extended Collection
            </Badge>
            <h2 className="font-playfair text-2xl md:text-3xl font-medium text-primary mb-4">
              REM7 Variants
            </h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              Explore our extended lineup of specialized models, each built on the proven REM7 platform.
            </p>
          </div>

          {/* Category Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full transition-all duration-300 ${
                  selectedCategory === category.id
                    ? `${category.color} text-white border-0 shadow-lg`
                    : "hover:shadow-md"
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>

          {/* Product Catalog */}
          <ProductCatalog selectedCategory={selectedCategory} />
        </section>

        {/* Comparison Banner */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-amber-50 via-white to-teal-50 rounded-2xl p-8 border border-border/30 text-center">
            <h3 className="font-playfair text-2xl md:text-3xl font-medium text-primary mb-4">
              Not Sure Which Model is Right for You?
            </h3>
            <p className="font-inter text-muted-foreground mb-6 max-w-2xl mx-auto">
              Use our comparison tool to see detailed specifications side-by-side and find your perfect smart toilet.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild className="bg-accent hover:bg-accent/90">
                <LocalizedLink to="/compare">
                  Compare All Models
                </LocalizedLink>
              </Button>
              <Button variant="outline" asChild>
                <LocalizedLink to="/technology">
                  Explore Technology
                </LocalizedLink>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 rounded-2xl p-8 sm:p-12 text-center border border-border/10">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Need Help Choosing?
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
              Our luxury bathroom specialists are ready to help you select the perfect REM7 smart toilet for your needs. Get expert advice, detailed specifications, and personalized recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button size="lg" asChild className="min-w-[200px]">
                <LocalizedLink to="/contact">
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Expert
                </LocalizedLink>
              </Button>
              <Button size="lg" variant="outline" asChild className="min-w-[200px]">
                <a href="https://wa.me/34642278939?text=Hello%2C%20I%27d%20like%20to%20learn%20more%20about%20your%20REM7%20smart%20toilet%20collection." target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick}>
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </a>
              </Button>
            </div>
            <div className="pt-6 border-t border-border/20">
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <a 
                  href="mailto:info@aureum7trading.com" 
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                >
                  <Mail className="w-4 h-4" />
                  <span>info@aureum7trading.com</span>
                </a>
                <a 
                  href="tel:+34642278939"
                  onClick={trackPhoneClick}
                  className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                >
                  <Phone className="w-4 h-4" />
                  <span>+34 642 278 939</span>
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-4">Response within 24 hours • Expert consultation available</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — emits FAQPage JSON-LD */}
      <FAQSection
        heading="Smart Toilets — Frequently Asked Questions"
        intro="What buyers, developers and architects most often ask about our luxury smart toilet range."
        faqs={[
          {
            question: "What is a luxury smart toilet and how does it differ from a standard bidet?",
            answer:
              "A luxury smart toilet integrates the WC, bidet, heated seat, warm-air dryer, deodoriser and controls into a single ceramic unit. Unlike a standalone bidet, it delivers instant-heated water, per-user presets, UV self-cleaning nozzles and touchless auto open/close — replacing three fixtures with one.",
          },
          {
            question: "Which model is Aureum VII Trading's flagship?",
            answer:
              "The LUXA M80 is the flagship: a tankless floor-standing smart toilet with motion-sensor auto lid, adjustable heated bidet, warm-air dryer, UV nozzle sterilisation, IPX4 protection and a 5-year warranty. It is available at €600 per unit.",
          },
          {
            question: "Do LUXA and REM7 smart toilets require special plumbing or electrical work?",
            answer:
              "They use standard EU water inlets (G1/2) and a grounded 220–240 V IPX4 socket within ~1 m of the unit. No tank means simpler in-wall carriers and lower profile than traditional cistern setups. A licensed plumber typically installs a unit in 2–3 hours.",
          },
          {
            question: "How long is delivery across the EU?",
            answer:
              "Air freight lead time is 10–15 business days to Spain, France, Germany, Italy, UK, Portugal, Netherlands, Belgium and Austria. Bulk container shipments (developer/hotel quantities) run approximately 40 days.",
          },
          {
            question: "What warranty and after-sales support do you provide?",
            answer:
              "All smart toilet models ship with a 5-year comprehensive warranty covering electronics, heating and ceramic body. Aureum VII Trading provides EU-based B2B support, spare parts, installation guides and CAD/BIM files for architects on request.",
          },
          {
            question: "Do you offer MOQ pricing for hotels, developers and OEM projects?",
            answer:
              "Yes. Volume pricing applies from 20+ units, with OEM customisation available for hospitality and developer projects. Request a project quote via info@aureum7trading.com or WhatsApp +34 642 278 939.",
          },
        ]}
      />

      <Footer />
    </div>
  );
}
